import React from 'react';
import { CheckCircle2, ArrowRight, Users, Award, Globe, Code2, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, expertise, images } from '../data/mock';

const About = () => {
  return (
    <section id="about" className="relative py-24 bg-slate-50 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 pattern-dots" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Side - Business Data Analysis */}
          <div className="relative">
            {/* Main Image - Data Analysis Dashboard */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-blue-500/10">
              <img
                src={images.team}
                alt="Business Data Analysis Dashboard"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
            </div>
            
            {/* Secondary Image - Analytics Graphs */}
            <div className="absolute -bottom-8 -right-8 w-52 h-40 rounded-2xl overflow-hidden shadow-xl border-4 border-white">
              <img
                src={images.dataAnalysis}
                alt="Analytics Graphs"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Stats Card */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-5 shadow-xl shadow-blue-500/10 border border-slate-100">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900 font-display">100%</div>
                  <div className="text-sm text-slate-500">Data-Driven</div>
                </div>
              </div>
            </div>

            {/* Decorative */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-blue-200 rounded-2xl" />
          </div>

          {/* Content Side */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
              <span className="text-blue-700 text-xs font-semibold uppercase tracking-wider">About Us</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 font-display leading-tight">
              Your Trusted
              <span className="block gradient-text">Software Partner</span>
            </h2>
            
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              ANCARTA TECHNOLOGIES is a leading software consulting company delivering enterprise-grade solutions. 
              We specialize in digital transformation, custom software development, and data-driven business intelligence.
            </p>
            
            <p className="text-slate-500 mb-8 leading-relaxed">
              With over two decades of experience, we've helped governments and enterprises worldwide 
              build secure, scalable, and innovative technology solutions powered by analytics and insights.
            </p>

            {/* Expertise List */}
            <div className="space-y-3 mb-8">
              {expertise.slice(0, 4).map((item, index) => (
                <div key={index} className="flex items-start gap-3 group">
                  <div className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center mt-0.5 flex-shrink-0 group-hover:bg-blue-500 transition-colors">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-slate-700 group-hover:text-slate-900 transition-colors">{item}</span>
                </div>
              ))}
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-6 mb-8 p-6 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <div className="text-center">
                <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-slate-900 font-display">50+</div>
                <div className="text-xs text-slate-500">Experts</div>
              </div>
              <div className="text-center border-x border-slate-100">
                <Award className="w-6 h-6 text-cyan-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-slate-900 font-display">50+</div>
                <div className="text-xs text-slate-500">Projects</div>
              </div>
              <div className="text-center">
                <Globe className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-slate-900 font-display">15+</div>
                <div className="text-xs text-slate-500">Countries</div>
              </div>
            </div>

            <Button
              onClick={() => document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 py-5 rounded-xl transition-all group"
            >
              Explore Services
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
